import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  List,
  Paper,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  Alert,
  Tabs,
  Tab,
  Stack,
  Badge,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  CircularProgress,
  Card,
  CardContent,
} from "@mui/material";
import {
  ContentCopy as CopyIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Delete as DeleteIcon,
  Description as DescriptionIcon,
  Download as DownloadIcon,
  ImageOutlined as ImageOutlinedIcon,
  PictureAsPdfOutlined as PictureAsPdfOutlinedIcon,
  InsertDriveFile as InsertDriveFileIcon,
} from "@mui/icons-material";
import { useUser } from "../UserContext";
import PushPinIcon from "@mui/icons-material/PushPin";
import LockIcon from "@mui/icons-material/Lock";
import PublicIcon from "@mui/icons-material/Public";
import { AgentPinnedPrompt } from "../../types";
import { API_BASE, getAuthToken } from "../../api/agentApi";

// Add an interface for your Artifacts
export interface Artifact {
  id: string;
  name: string;
  filename: string;
  type: string;
  url?: string;
  agent_id?: string;
  agent_version?: number;
  run_number?: number;
}

// Secure helper function to fetch artifact image with Authorization header
// Returns blob URL instead of exposing JWT in query params
const fetchSecureArtifactImage = async (artifact: Artifact): Promise<string | undefined> => {
  try {
    if (
      !artifact.agent_id ||
      !artifact.agent_version ||
      !artifact.run_number ||
      !artifact.filename
    ) {
      return artifact.url || undefined;
    }

    const token = getAuthToken();
    const url = `${API_BASE}/agent/${artifact.agent_id}/version/${artifact.agent_version}/runs/${artifact.run_number}/artifact/${encodeURIComponent(artifact.filename)}`;
    
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      console.error(`Failed to fetch artifact image: ${response.status}`);
      return undefined;
    }

    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    return blobUrl;
  } catch (error) {
    console.error("Failed to fetch secure artifact image:", error);
    return undefined;
  }
};

// Helper function to determine artifact type
const getArtifactType = (artifact: Artifact): string => {
  if (artifact.type) return artifact.type;
  const filename = artifact.filename?.toLowerCase() || "";
  if (
    ["jpg", "jpeg", "png", "gif", "svg", "webp"].some((ext) =>
      filename.endsWith(`.${ext}`),
    )
  )
    return "image";
  if (filename.endsWith(".pdf")) return "pdf";
  if (filename.endsWith(".csv")) return "csv";
  if (filename.endsWith(".json")) return "json";
  if (filename.endsWith(".txt")) return "text";
  return "file";
};

// A modern Card-style Artifact Item
const ArtifactCard: React.FC<{
  artifact: Artifact;
  onDownload?: (artifact: Artifact) => void;
}> = ({ artifact, onDownload }) => {
  const artifactType = getArtifactType(artifact);
  const isImage = artifactType === "image";
  const [imageUrl, setImageUrl] = useState<string>("");
  const [imageLoading, setImageLoading] = useState<boolean>(false);

  // Fetch secure image when component mounts or artifact changes
  useEffect(() => {
    let isMounted = true;
    let blobUrl: string | undefined;

    const loadImage = async () => {
      if (isImage) {
        setImageLoading(true);
        const url = await fetchSecureArtifactImage(artifact);
        if (isMounted && url) {
          blobUrl = url;
          setImageUrl(url);
        }
        setImageLoading(false);
      }
    };

    loadImage();

    // Cleanup: revoke blob URL when component unmounts or artifact changes
    return () => {
      isMounted = false;
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [artifact, isImage]);

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: "12px",
        overflow: "hidden",
        transition: "0.2s",
        "&:hover": { transform: "translateY(-2px)", boxShadow: 3 },
      }}
    >
      {/* Visual Preview Area */}
      <Box
        sx={{
          height: 120,
          bgcolor: "#f5f5f5",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {isImage ? (
          imageLoading ? (
            <CircularProgress size={24} />
          ) : imageUrl ? (
            <img
              src={imageUrl}
              alt={artifact.filename}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <ImageOutlinedIcon sx={{ fontSize: 40, color: "text.disabled" }} />
          )
        ) : artifactType === "pdf" ? (
          <PictureAsPdfOutlinedIcon
            sx={{ fontSize: 40, color: "error.main" }}
          />
        ) : (
          <DescriptionIcon sx={{ fontSize: 40, color: "text.disabled" }} />
        )}
      </Box>

      <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
        <Typography
          variant="caption"
          color="primary"
          sx={{ fontWeight: 700, display: "block", mb: 0.5 }}
        >
          {artifactType}
        </Typography>

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ width: "100%", overflow: "hidden" }}
        >
          <Typography
            variant="body2"
            noWrap // This is key: it adds the "..." instead of wrapping
            sx={{ flexGrow: 1, minWidth: 0 }}
          >
            {artifact.filename || artifact.name}
          </Typography>

          <Tooltip title="Download">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDownload?.(artifact);
              }}
              sx={{
                bgcolor: "primary.main",
                color: "white",
                "&:hover": { bgcolor: "primary.dark" },
                borderRadius: "6px",
                p: 0.5, // Reduced padding to keep it small
                flexShrink: 0, // Prevents the icon from squishing
              }}
            >
              <DownloadIcon sx={{ fontSize: "1rem" }} />
            </IconButton>
          </Tooltip>
        </Stack>

        {/* <Typography
          variant="body2"
          sx={{ fontWeight: 600, mb: 1 }}
          noWrap
          title={artifact.filename}
        >
          {artifact.filename || artifact.name}
        </Typography> */}

        {/* <Stack direction="row" spacing={1}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onDownload?.(artifact);
            }}
            sx={{
              bgcolor: "primary.main",
              color: "white",
              "&:hover": { bgcolor: "primary.dark" },
              borderRadius: "8px",
              px: 1.5,
            }}
          >
            <DownloadIcon fontSize="small" sx={{ mr: 0.5 }} />
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              Download
            </Typography>
          </IconButton>
        </Stack> */}
      </CardContent>
    </Card>
  );
};

const ArtifactsAndPinnedPromptsSideBar: React.FC<{
  prompts: AgentPinnedPrompt[];
  artifacts?: Artifact[]; // New Prop
  onCopy: (text: string) => void;
  onDelete: (promptId: string) => void;
  onArtifactClick?: (artifact: Artifact) => void; // New Prop
  deleting?: string;
  error?: any;
}> = ({
  prompts,
  artifacts = [],
  onCopy,
  onDelete,
  onArtifactClick,
  deleting,
  error,
}) => {
  const { user } = useUser();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [selectedPrompt, setSelectedPrompt] =
    useState<AgentPinnedPrompt | null>(null);

  // Tab State: 0 = Artifacts, 1 = Pinned Prompts
  const [activeTab, setActiveTab] = useState(0);

  // Auto-switch to Artifacts tab when new ones arrive
  useEffect(() => {
    if (artifacts.length > 0) {
      setActiveTab(0);
    }
  }, [artifacts.length]);

  const handleOpenModal = (prompt: AgentPinnedPrompt) =>
    setSelectedPrompt(prompt);
  const handleCloseModal = () => setSelectedPrompt(null);

  if (isCollapsed) {
    return (
      <Box
        sx={{
          width: 50,
          height: "100%",
          borderLeft: "1px solid",
          borderColor: "divider",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          pt: 1,
        }}
      >
        <Tooltip title="Expand Sidebar">
          <IconButton onClick={() => setIsCollapsed(false)} size="small">
            <ChevronLeftIcon />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: { xs: "260px", sm: "280px", md: "320px" },
        minWidth: { xs: "260px", sm: "280px", md: "320px" },
        maxWidth: { xs: "260px", sm: "280px", md: "320px" },
        height: "100%",
        borderLeft: "1px solid",
        borderColor: "divider",
        display: "flex",
        flexDirection: "column",
        pt: 1,
        overflow: "hidden",
        flexShrink: 0, // Prevent this sidebar from shrinking
      }}
    >
      {/* Header Actions */}
      <Box
        sx={{
          px: 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, val) => setActiveTab(val)}
          sx={{
            minHeight: 40,
            "& .MuiTab-root": {
              textTransform: "none",
              minWidth: 100,
              fontWeight: 600,
            },
          }}
        >
          <Tab
            label={
              <Badge
                badgeContent={artifacts.length}
                color="primary"
                sx={{ "& .MuiBadge-badge": { right: -10, top: 0 } }}
              >
                Artifacts
              </Badge>
            }
          />
          <Tab label="Pinned Prompts" />
        </Tabs>
        <Tooltip title="Collapse">
          <IconButton onClick={() => setIsCollapsed(true)} size="small">
            <ChevronRightIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ flexGrow: 1, overflowY: "auto", px: 2, pt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* TAB 0: ARTIFACTS VIEW */}
        {activeTab === 0 && (
          <Box>
            {artifacts.length === 0 ? (
              <Box sx={{ textAlign: "center", pt: 8, color: "text.secondary" }}>
                <DescriptionIcon sx={{ fontSize: 48, mb: 2, opacity: 0.2 }} />
                <Typography variant="body2">
                  No artifacts generated yet
                </Typography>
              </Box>
            ) : (
              <Stack spacing={2}>
                {artifacts
                  .filter((art) => {
                    // Exclude system files
                    const excludedFiles = [
                      "console.out",
                      "prompt.txt",
                      "run.out",
                      "timestamp.txt",
                    ];
                    const filename = art.filename || art.name;
                    return !excludedFiles.includes(filename?.toLowerCase());
                  })
                  .map((artifact) => (
                    <ArtifactCard
                      key={artifact.id}
                      artifact={artifact}
                      onDownload={onArtifactClick}
                    />
                  ))}
              </Stack>
            )}
          </Box>
        )}

        {/* TAB 1: PINNED PROMPTS VIEW */}
        {activeTab === 1 && (
          <Box>
            {prompts.length === 0 ? (
              <Box sx={{ textAlign: "center", pt: 8, color: "text.secondary" }}>
                <PushPinIcon sx={{ fontSize: 48, mb: 2, opacity: 0.2 }} />
                <Typography variant="body2">No pinned prompts yet</Typography>
              </Box>
            ) : (
              <List sx={{ p: 0 }}>
                {prompts.map((prompt) => (
                  <Paper key={prompt.id} elevation={1} sx={{ p: 1.5, mb: 1.5 }}>
                    <Box display="flex" alignItems="flex-start" mb={0.5}>
                      <PushPinIcon
                        fontSize="small"
                        sx={{ mr: 0.5, color: "primary.main" }}
                      />
                      {prompt.is_public ? (
                        <Tooltip title="Public">
                          <PublicIcon
                            fontSize="small"
                            sx={{ mr: 0.7, color: "success.main" }}
                          />
                        </Tooltip>
                      ) : (
                        <Tooltip title="Private">
                          <LockIcon
                            fontSize="small"
                            sx={{ mr: 0.7, color: "error.main" }}
                          />
                        </Tooltip>
                      )}
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body2">
                          {prompt.prompt_text.length > 100 ? (
                            <>
                              {prompt.prompt_text.substring(0, 100)}...
                              <Button
                                size="small"
                                onClick={() => handleOpenModal(prompt)}
                                sx={{ p: 0, ml: 1, textTransform: "none" }}
                              >
                                Show More
                              </Button>
                            </>
                          ) : (
                            prompt.prompt_text
                          )}
                        </Typography>
                      </Box>
                      {user?.id === prompt.created_by && (
                        <IconButton
                          onClick={() => onDelete(prompt.id)}
                          size="small"
                          disabled={deleting === prompt.id}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mt={1}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {prompt.created_at.substring(0, 10)}
                      </Typography>
                      <Button
                        onClick={() => onCopy(prompt.prompt_text)}
                        size="small"
                        startIcon={<CopyIcon fontSize="small" />}
                        sx={{ textTransform: "none", fontSize: "0.75rem" }}
                      >
                        Copy
                      </Button>
                    </Box>
                  </Paper>
                ))}
              </List>
            )}
          </Box>
        )}
      </Box>

      {/* Detail Dialog */}
      <Dialog
        open={Boolean(selectedPrompt)}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {selectedPrompt?.is_public ? "Public" : "Private"} Pinned Prompt
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
            {selectedPrompt?.prompt_text}
          </Typography>
          <Box
            sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 1 }}
          >
            <Button
              onClick={() => {
                onCopy(selectedPrompt!.prompt_text);
                handleCloseModal();
              }}
              startIcon={<CopyIcon />}
              variant="outlined"
            >
              Copy
            </Button>
            <Button onClick={handleCloseModal} variant="contained">
              Close
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ArtifactsAndPinnedPromptsSideBar;
